import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_PREFIX = 'cai-quote-cart:'

/** Deduplicated indication ids behind a set of cart keys — the ids checkout applies to. */
export function indicationIdsFromKeys(cartKeys: Iterable<string>): string[] {
  const ids: string[] = []
  for (const key of cartKeys) {
    const id = key.split(':')[0]
    if (id && !ids.includes(id)) ids.push(id)
  }
  return ids
}

function readStoredKeys(applicationId: string): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + applicationId)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []
  } catch {
    return []
  }
}

function writeStoredKeys(applicationId: string, cartKeys: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + applicationId, JSON.stringify([...cartKeys]))
  } catch {
    // A full or blocked localStorage just costs us the persisted cart.
  }
}

export interface QuoteCartValue {
  /** `${indicationId}:${coverageType}` per added coverage — see `coverageCartKey`. */
  keys: ReadonlySet<string>
  count: number
  indicationIds: string[]
  isOpen: boolean
  add: (cartKeys: string[]) => void
  remove: (cartKeys: string[]) => void
  clear: () => void
  open: () => void
  close: () => void
}

const QuoteCartContext = createContext<QuoteCartValue | null>(null)

interface QuoteCartProviderProps {
  children: ReactNode
  /**
   * Persists the cart under this application, so a reload keeps it. Arrives late (the
   * provider sits above the fetch) and changes as staff move between customers — both
   * re-read from storage, which is why the cart is never keyed on the provider itself.
   */
  applicationId?: string
}

/** Cart of added coverages; `date_applied` on the indication is the record once checked out. */
export function QuoteCartProvider({ children, applicationId }: QuoteCartProviderProps) {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set())
  const [isOpen, setIsOpen] = useState(false)
  const [hydratedFor, setHydratedFor] = useState<string>()

  useEffect(() => {
    if (!applicationId || applicationId === hydratedFor) return
    setKeys(new Set(readStoredKeys(applicationId)))
    setHydratedFor(applicationId)
  }, [applicationId, hydratedFor])

  // Waits on the hydration above landing: writing this application's empty initial set
  // first would erase the cart it is about to read.
  useEffect(() => {
    if (!applicationId || applicationId !== hydratedFor) return
    writeStoredKeys(applicationId, keys)
  }, [applicationId, hydratedFor, keys])

  const add = useCallback((cartKeys: string[]) => {
    setKeys((current) => {
      const next = new Set(current)
      cartKeys.forEach((key) => next.add(key))
      return next
    })
  }, [])

  const remove = useCallback((cartKeys: string[]) => {
    setKeys((current) => {
      const next = new Set(current)
      cartKeys.forEach((key) => next.delete(key))
      return next
    })
  }, [])

  const clear = useCallback(() => setKeys(new Set()), [])
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  const value = useMemo<QuoteCartValue>(
    () => ({
      keys,
      count: keys.size,
      indicationIds: indicationIdsFromKeys(keys),
      isOpen,
      add,
      remove,
      clear,
      open,
      close,
    }),
    [keys, isOpen, add, remove, clear, open, close]
  )

  return <QuoteCartContext.Provider value={value}>{children}</QuoteCartContext.Provider>
}

export function useQuoteCart(): QuoteCartValue {
  const cart = useContext(QuoteCartContext)
  if (!cart) throw new Error('useQuoteCart must be used inside a QuoteCartProvider')
  return cart
}
